'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useReducer } from 'react';
import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { postNotification } from 'ReduxMessenger/actions/NotificationActions';
import { Networks, TrackingStatusCodes } from 'AdsUI/lib/Constants';
import store from 'AdsUI/redux-store';
import TrackingStatusParser from 'AdsUI/lib/TrackingStatusParser';
import TrackingStatusEntityRecord from 'AdsUI/records/reporting/TrackingStatusEntityRecord';
import TrackingStatusRecord from 'AdsUI/records/reporting/TrackingStatusRecord';
import TrackingErrorRecord from 'AdsUI/records/reporting/TrackingErrorRecord';
import AdBasicRecord from 'AdsUI/records/reporting/AdBasicRecord';
import CampaignBasicRecord from 'AdsUI/records/reporting/CampaignBasicRecord';
import getErrorNotificationBody from 'AdsUI/lib/ErrorNotificationBody';
import logFailedAction from 'AdsUI/lib/logFailedAction';
import { TrackingErrorReasons } from 'AdsUI/records/reporting/TrackingErrorRecord';
import { TRACKING_ERROR_NETWORK_MAPPER } from '../records/reporting/TrackingErrorRecord';
const FIXABLE_ERRORS = {
  ENGAGEMENTS: [TrackingErrorReasons.FB_TOO_MANY_ENGAGEMENTS, TrackingErrorReasons.FB_INSTAGRAM_STORY_ENGAGEMENTS],
  DESTINATION_URL: [TrackingErrorReasons.NO_REDIRECT, TrackingErrorReasons.NO_URLS_FOUND, TrackingErrorReasons.UNABLE_TO_PARSE_URL, TrackingErrorReasons.TOO_MANY_REDIRECTS],
  PERMISSIONS: [TrackingErrorReasons.NO_PERMISSION, TrackingErrorReasons.NO_PAGE_PERMISSION, TrackingErrorReasons.FB_CUSTOM_AUDIENCE_TERMS, TrackingErrorReasons.FB_INVALID_PARAMETERS, TrackingErrorReasons.FB_INVALID_NEGATIVE_TARGETING, TrackingErrorReasons.LI_NO_PERMISSION, TrackingErrorReasons.FB_NO_PERMISSION],
  UNTRACEABLE: [TrackingErrorReasons.FB_UNSUPPORTED_AD],
  GOOGLE_TRACKING_CUSTOMIZED: [TrackingErrorReasons.GOOGLE_EDITED_TEMPLATE],
  UNSUPPORTED_CREATIVE: [TrackingErrorReasons.FB_INVALID_IMAGE_CROP, TrackingErrorReasons.UNSUPPORTED_CTA, TrackingErrorReasons.INVALID_POST_BODY, TrackingErrorReasons.GOOGLE_INVALID_POST_BODY, TrackingErrorReasons.FB_INVALID_TARGETING, TrackingErrorReasons.FB_DUPLICATE_USER_OR_MEDIA_OBJECT, TrackingErrorReasons.FB_ASSET_LABEL_ISSUE, TrackingErrorReasons.FB_DUPLICATE_AD_ASSET],
  TWO_FACTOR: [TrackingErrorReasons.TWO_FACTOR]
};
const UNFIXABLE_ERRORS = {
  DUPLICATE_CONTENT: [TrackingErrorReasons.LI_SHARED_CONTENT_EDITED_TRACKING],
  UNSUPPORTED_TYPE: [TrackingErrorReasons.TYPE_UNSUPPORTED_BY_API, TrackingErrorReasons.TYPE_NOT_TRACKABLE, TrackingErrorReasons.TYPE_UNKNOWN, TrackingErrorReasons.FB_INTERNAL_LINKS, TrackingErrorReasons.CREATIVE_UNSUPPORTED_BY_API],
  NETWORK_UNAVAILABLE: [TrackingErrorReasons.NETWORK_UNAVAILABLE, TrackingErrorReasons.FB_NETWORK_UNAVAILABLE],
  OTHER: [TrackingErrorReasons.API_ERROR, TrackingErrorReasons.UNKNOWN]
}; // Convert to a map like { FIXABLE_ERRORS: { ENGAGEMENTS: [], ... } }

const ERROR_MAPPING = Object.assign({}, Object.keys(FIXABLE_ERRORS).reduce((memo, errorCategory) => Object.assign({}, memo, {}, FIXABLE_ERRORS[errorCategory].reduce((memo2, error) => Object.assign({}, memo2, {
  [error]: {
    resolutionCategory: 'FIXABLE_ERRORS',
    errorCategory
  }
}), {})), {}), {}, Object.keys(UNFIXABLE_ERRORS).reduce((memo, errorCategory) => Object.assign({}, memo, {}, UNFIXABLE_ERRORS[errorCategory].reduce((memo2, error) => Object.assign({}, memo2, {
  [error]: {
    resolutionCategory: 'UNFIXABLE_ERRORS',
    errorCategory
  }
}), {})), {}));
const DEFAULT_CATEGORY = {
  resolutionCategory: 'UNFIXABLE_ERRORS',
  errorCategory: 'OTHER'
};
const NETWORK_AD_RECORD_MAP = {
  [Networks.FACEBOOK]: props => new AdBasicRecord(props),
  [Networks.LINKEDIN]: props => new AdBasicRecord(props),
  [Networks.ADWORDS]: CampaignBasicRecord
};
const FETCH_START = 'FETCH_START';
const FETCH_SUCCESS = 'FETCH_SUCCESS';
const FETCH_FAILED = 'FETCH_FAILED';

const convertIfNetworkIsAmbiguious = (errorReason, adNetwork) => {
  if (TRACKING_ERROR_NETWORK_MAPPER[errorReason] && TRACKING_ERROR_NETWORK_MAPPER[errorReason][adNetwork]) {
    return TRACKING_ERROR_NETWORK_MAPPER[errorReason][adNetwork];
  }

  return errorReason;
};

const useTrackingErrorModal = (fetchTrackingStatuses, allTrackingErrors) => {
  const [{
    error,
    loading,
    statuses
  }, dispatch] = useReducer((state, action) => {
    const {
      payload,
      type
    } = action;

    switch (type) {
      case FETCH_START:
        return Object.assign({}, state, {
          loading: true
        });

      case FETCH_SUCCESS:
        {
          const trackingStatuses = payload.trackingStatuses || payload.results;
          const statusRecords = trackingStatuses.map(({
            entity,
            trackingStatus
          }) => TrackingStatusEntityRecord({
            entity: NETWORK_AD_RECORD_MAP[entity.adNetwork](entity),
            trackingStatus: TrackingStatusParser.parse({
              untracked: [trackingStatus]
            })
          }));
          return Object.assign({}, state, {
            loading: false,
            statuses: statusRecords
          });
        }

      case FETCH_FAILED:
        return Object.assign({}, state, {
          loading: false,
          error: true
        });

      default:
        return state;
    }
  }, {});
  useEffect(() => {
    if (loading || statuses || error) {
      return;
    }

    dispatch({
      type: FETCH_START
    });
    fetchTrackingStatuses().then(response => dispatch({
      type: FETCH_SUCCESS,
      payload: response
    })).catch(({
      message,
      status,
      statusText,
      data
    }) => {
      let responseJSON;

      try {
        responseJSON = data && JSON.parse(data);
      } catch (err) {
        responseJSON = null;
      }

      logFailedAction({
        type: 'FETCH_TRACKING_STATUSES_FAILED',
        meta: {
          message,
          status,
          statusText,
          responseJSON
        }
      });
      dispatch({
        type: FETCH_FAILED
      });

      const _getErrorNotification = getErrorNotificationBody({
        actionType: 'danger',
        text: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "trackingStatusModal.couldNotFetchErrors"
        }),
        details: message
      }),
            notification = _objectWithoutPropertiesLoose(_getErrorNotification, ["type"]);

      store.dispatch(postNotification(Object.assign({
        id: 'USE_TRACKING_ERROR_MODAL_ERROR',
        type: 'danger'
      }, notification)));
    });
  }, [dispatch, error, fetchTrackingStatuses, loading, statuses]);
  const groupedStatuses = useMemo(() => {
    if (!statuses) {
      return null;
    }

    return statuses.filter(entityStatus => entityStatus.trackingStatus.code !== TrackingStatusCodes.TRACKED && ( // The server sends back all tracking statuses, but we've filtered them in code when showing
    // the badge.  Rather than use the same filter logic, we'll just pass in which errors we want
    // the modal to show.
    !allTrackingErrors || allTrackingErrors.find(trackingError => trackingError.campaignId === (entityStatus.entity.campaignId || entityStatus.entity.id) && trackingError.adAccount.accountId === entityStatus.entity.adAccountId && trackingError.adAccount.network === entityStatus.entity.adNetwork))).reduce((memo, entityStatus) => {
      const {
        trackingStatus,
        entity
      } = entityStatus;
      trackingStatus.statuses.forEach(({
        reason: errorReason
      }) => {
        const errorStatus = convertIfNetworkIsAmbiguious(errorReason, entity.adNetwork);
        const {
          resolutionCategory,
          errorCategory
        } = ERROR_MAPPING[errorStatus] || DEFAULT_CATEGORY;

        if (!ERROR_MAPPING[errorStatus]) {
          Raven.captureMessage('Missing tracking status', {
            extra: {
              errorStatus
            }
          });
        }

        const entityStatusRecord = new TrackingStatusEntityRecord({
          entity,
          trackingStatus: new TrackingStatusRecord({
            code: trackingStatus.code,
            lastAttempted: trackingStatus.lastAttempted,
            statuses: trackingStatus.statuses.map(status => new TrackingErrorRecord(Object.assign({}, status.toJS(), {
              reason: convertIfNetworkIsAmbiguious(status.reason, entity.adNetwork)
            }))).filter(status => status.reason === errorStatus)
          })
        });
        memo[resolutionCategory][errorCategory].push(entityStatusRecord);
      });
      return memo;
    }, {
      FIXABLE_ERRORS: Object.keys(FIXABLE_ERRORS).reduce((memo, err) => {
        memo[err] = [];
        return memo;
      }, {}),
      UNFIXABLE_ERRORS: Object.keys(UNFIXABLE_ERRORS).reduce((memo, err) => {
        memo[err] = [];
        return memo;
      }, {})
    });
  }, [allTrackingErrors, statuses]);
  return {
    error,
    loading,
    groupedStatuses
  };
};

export default useTrackingErrorModal;